import { create } from "zustand";
import { getEffectiveVendUserSnapshot } from "../effectiveUser";
import { ApiResponseError } from "../../apiError";
import type { TravelRecord } from "../api/task/travel";

import {
  fetchExecutionRoute,
  fetchLiveExecutions,
  type ExecutionRoute,
  type LiveExecution,
} from "../api/executionLocation";

type ExecutionLocationState = {
  live: LiveExecution[];
  liveTrips: TravelRecord[];
  routes: Record<string, ExecutionRoute>;
  routeLoadState: Record<string, "loading" | "loaded" | "error">;
  liveLoaded: boolean;
  liveError: string | null;
  loadLive: (force?: boolean) => Promise<void>;
  loadRoute: (taskId: string, force?: boolean, scope?: "growth") => Promise<void>;
  refreshLoaded: () => Promise<void>;
  reset: () => void;
};

const inFlightRoutes = new Map<string, Promise<void>>();
let liveInFlight: Promise<void> | null = null;
let storeGeneration = 0;
let liveRefreshQueued = false;
let liveFetchedAt = 0;
const routeRefreshQueued = new Set<string>();

export const executionRouteKey = (taskId: string, scope?: "growth") => scope ? `${scope}:${taskId}` : taskId;

export const useExecutionLocationStore = create<ExecutionLocationState>((set, get) => ({
  live: [],
  liveTrips: [],
  routes: {},
  routeLoadState: {},
  liveLoaded: false,
  liveError: null,
  loadLive: (force = false) => {
    if (getEffectiveVendUserSnapshot().organization?.executionLiveWebEnabled === false) {
      set({ live: [], liveTrips: [], liveLoaded: false, liveError: null });
      return Promise.resolve();
    }
    if (liveInFlight) {
      if (force) liveRefreshQueued = true;
      return liveInFlight;
    }
    if (!force && get().liveLoaded && Date.now() - liveFetchedAt < 10_000) return Promise.resolve();
    const generation = storeGeneration;
    const request = (async () => {
      do {
        liveRefreshQueued = false;
        const { items, trips = [] } = await fetchLiveExecutions();
        if (generation !== storeGeneration) return;
        if (getEffectiveVendUserSnapshot().organization?.executionLiveWebEnabled === false) {
          set({ live: [], liveTrips: [], liveLoaded: false, liveError: null });
          return;
        }
        if (!liveRefreshQueued) {
          liveFetchedAt = Date.now();
          set({ live: items, liveTrips: trips, liveLoaded: true, liveError: null });
        }
      } while (liveRefreshQueued);
    })()
      .catch(error => {
        if (generation === storeGeneration) set({
          liveError: error instanceof Error ? error.message : "Could not load live locations.",
          ...(error instanceof ApiResponseError && [401, 403].includes(error.status) ? { live: [], liveTrips: [], liveLoaded: false } : {}),
        });
        throw error;
      })
      .finally(() => {
        if (liveInFlight === request) liveInFlight = null;
      });
    liveInFlight = request;
    return request;
  },
  loadRoute: (taskId, force = false, scope) => {
    const key = executionRouteKey(taskId, scope);
    if (!force && get().routes[key]) return Promise.resolve();
    const existing = inFlightRoutes.get(key);
    if (existing) {
      if (force) routeRefreshQueued.add(key);
      return existing;
    }
    const generation = storeGeneration;
    set((state) => ({
      routeLoadState: { ...state.routeLoadState, [key]: "loading" },
    }));
    const request = (async () => {
      do {
        routeRefreshQueued.delete(key);
        const route = await (scope ? fetchExecutionRoute(taskId, 1_000, scope) : fetchExecutionRoute(taskId));
        if (generation !== storeGeneration) return;
        if (!routeRefreshQueued.has(key)) {
          set((state) => ({
            routes: { ...state.routes, [key]: route },
            routeLoadState: { ...state.routeLoadState, [key]: "loaded" },
          }));
        }
      } while (routeRefreshQueued.has(key));
    })()
      .catch((error) => {
        if (generation === storeGeneration) {
          set((state) => {
            const denied = error instanceof ApiResponseError && [401, 403].includes(error.status);
            const routes = denied ? { ...state.routes } : state.routes;
            if (denied) delete routes[key];
            return { routes, routeLoadState: { ...state.routeLoadState, [key]: "error" } };
          });
        }
        throw error;
      })
      .finally(() => {
        if (inFlightRoutes.get(key) === request) inFlightRoutes.delete(key);
      });
    inFlightRoutes.set(key, request);
    return request;
  },
  refreshLoaded: async () => {
    const state = get();
    await Promise.all([
      ...(state.liveLoaded || liveInFlight ? [state.loadLive(true)] : []),
      ...Object.keys({ ...state.routeLoadState, ...state.routes }).map((key) => key.startsWith("growth:")
        ? state.loadRoute(key.slice("growth:".length), true, "growth")
        : state.loadRoute(key, true)),
    ]);
  },
  reset: () => {
    storeGeneration += 1;
    liveInFlight = null;
    liveRefreshQueued = false;
    liveFetchedAt = 0;
    routeRefreshQueued.clear();
    inFlightRoutes.clear();
    set({ live: [], liveTrips: [], routes: {}, routeLoadState: {}, liveLoaded: false, liveError: null });
  },
}));
